import React, { useState, useEffect } from 'react'
import { formatDateLong } from '@/utils/dateFormat'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'
import { useNotifications } from '@/hooks/useNotifications'
import { PageHeader } from '@/components/ui/PageHeader'
import { ArrowRight } from 'lucide-react'

const Home: React.FC = () => {
  const { user } = useAuth()
  const { notifications, unreadCount } = useNotifications()
  const [currentDate, setCurrentDate] = useState<string>('')

  useEffect(() => {
    const date = new Date()
    setCurrentDate(formatDateLong(date))
  }, [])

  // Mock activity data
  const activityItems = [
    { id: 1, type: 'user_created', message: 'New user John Smith added to Engineering OU', timestamp: '2 hours ago' },
    { id: 2, type: 'company_sync', message: 'Company data synced from source system', timestamp: '4 hours ago' },
    { id: 3, type: 'project_created', message: 'New project "Platform Upgrade" created', timestamp: '1 day ago' },
    { id: 4, type: 'user_deactivated', message: 'User Jane Doe deactivated', timestamp: '2 days ago' },
    { id: 5, type: 'ou_sync', message: 'OU sync completed: 150 users synced', timestamp: '3 days ago' },
  ]

  // Bug #367: Settings is AdminRoute-guarded, so only offer it to platform admins.
  const isPlatformAdmin = !!user?.roles?.includes(UserRole.PlatformAdmin)
  const quickLinks = [
    { label: 'Users', icon: '👥', path: '/users' },
    { label: 'Companies', icon: '🏢', path: '/companies' },
    { label: 'Projects', icon: '📋', path: '/projects' },
    ...(isPlatformAdmin ? [{ label: 'Settings', icon: '⚙️', path: '/settings' }] : []),
  ]

  return (
    <div style={{ padding: '0' }}>
      <PageHeader title={`Welcome back, ${user?.displayName?.split(' ')[0] || 'there'}`} subtitle={currentDate} />

      <div style={{ padding: '24px' }}>
        {/* Main grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          {/* Recent activity card */}
          <div
            style={{
              gridColumn: 'span 2',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '8px',
              padding: '24px',
              backgroundColor: 'var(--color-white)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            <h2 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
              Recent activity
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {activityItems.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--color-white)',
                    borderLeft: '3px solid var(--color-magenta)',
                    fontSize: '14px',
                    color: 'var(--color-dark-grey)',
                  }}
                >
                  <div style={{ fontWeight: 500, color: 'var(--color-black)', marginBottom: '4px' }}>
                    {item.message}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>{item.timestamp}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Notifications card */}
          <div
            style={{
              border: '1px solid var(--color-light-grey)',
              borderRadius: '8px',
              padding: '24px',
              backgroundColor: 'var(--color-white)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--color-black)' }}>
                Notifications
              </h2>
              {unreadCount > 0 && (
                <span
                  style={{
                    backgroundColor: 'var(--color-magenta)',
                    color: 'var(--color-white)',
                    borderRadius: '12px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {notifications.slice(0, 5).map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    padding: '10px',
                    backgroundColor: notification.isRead ? 'var(--color-white)' : '#faf8fb',
                    borderLeft: notification.isRead ? '3px solid var(--color-light-grey)' : '3px solid var(--color-magenta)',
                    fontSize: '13px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 500, color: 'var(--color-black)', marginBottom: '2px' }}>
                    {notification.title}
                  </div>
                  <div style={{ color: 'var(--color-dark-grey)', fontSize: '12px' }}>{notification.message}</div>
                </div>
              ))}
            </div>

            <a
              href="/notifications"
              style={{
                display: 'inline-block',
                marginTop: '16px',
                color: 'var(--color-magenta)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              View all
            </a>
          </div>
        </div>

        {/* Quick links section */}
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
            Quick links
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '16px',
            }}
          >
            {quickLinks.map((link) => (
              <a
                key={link.label}
                href={link.path}
                style={{
                  padding: '20px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-white)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget
                  el.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'
                  el.style.borderColor = 'var(--color-magenta)'
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget
                  el.style.boxShadow = 'none'
                  el.style.borderColor = 'var(--color-light-grey)'
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{link.icon}</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-black)' }}>
                  {link.label}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home
